import { type Mapping, type MappingItem } from '@pn/core/domain/data';
import type {
  Filter,
  ScrollableQuery,
  Sort,
  StreamableQuery,
} from '@pn/core/domain/query';
import type { UnitSystem } from '@pn/core/domain/types';
import { toSIUnit, undoUnitConversion } from '@pn/core/domain/units';
import { assert } from '@pn/core/utils/assert';
import { oneline } from '@pn/core/utils/string';
import { toListReferencePredicate } from '@pn/services/api/query/listReferenceSql';
import {
  clamp,
  isBoolean,
  isEmpty,
  isNil,
  isNumber,
  isString,
} from 'lodash-es';

type ApiQuery = {
  data_type: string;
  sql: string;
};

export const apiQueryMapper = (mapping: Mapping) => {
  return {
    toTargetQuery: (
      query: ScrollableQuery | StreamableQuery,
      forcePagination = false
    ): ApiQuery => {
      const whereAdditions = buildAdditionalWhereClause(query, mapping);
      const orderByClause = buildOrderByClause(query.sorts);

      if (!isNil(query.sql)) {
        let sql = query.sql;

        if (!isEmpty(query.fields)) {
          sql = sql.replace(
            /\bSELECT\b[\s\S]+?\bFROM\b/i,
            `SELECT ${buildColumnsClause(query.fields)} FROM`
          );
        }

        sql = injectWhereAdditions(sql, whereAdditions);
        sql = applyOrderByOverride(sql, orderByClause);

        if (forcePagination && 'pageSize' in query) {
          sql = applyForcedPagination(sql, query);
        }

        return { data_type: query.dataType, sql };
      }

      const offset = 'page' in query ? query.page * query.pageSize : -1;
      const limit =
        'pageSize' in query ? `OFFSET ${offset} LIMIT ${query.pageSize}` : '';

      return {
        data_type: query.dataType,
        sql: oneline`
          SELECT ${buildColumnsClause(query.fields)} FROM data_table
          ${whereAdditions ? `WHERE ${whereAdditions}` : ''}
          ${orderByClause ?? 'ORDER BY "internal_id"'}
          ${limit}
        `,
      };
    },
  };
};

function buildColumnsClause(fields: string[]): string {
  return isEmpty(fields) ? '*' : fields.map((f) => `"${f}"`).join(', ');
}

/**
 * Builds the combined scopeIds + filters + geometry constraint (without
 * the `WHERE` keyword), shared by both the raw-sql injection path and the
 * from-scratch SELECT construction path.
 */
function buildAdditionalWhereClause(
  query: ScrollableQuery | StreamableQuery,
  mapping: Mapping
): string | undefined {
  const filterClauses = query.filters
    .filter(
      (f) => f.operator === 'isNil' || (!isNil(f.value) && f.value !== '')
    )
    .map((f) => {
      const mappingItem = mapping[f.field];
      assert(mappingItem, `Mapping not found for field ${f.field}`);

      const value = toFilterValue({
        mappingItem,
        fromValue: f.value,
        unitSystem: query.unitSystem,
      });

      return toFilterClause(f.operator, f.field, value);
    });

  const linkOp = query.filtersLinkOperator.toUpperCase() as 'AND' | 'OR';

  const parts: string[] = [];

  if (!isNil(query.sourceListId)) {
    /**
     * The API expands list references to the list's current members, so the
     * ids `loadItem` resolved into `scopeIds` (kept for the UI counts and
     * local processing) don't need to be sent.
     */
    parts.push(toListReferencePredicate(query.sourceListId));
  } else if (!isEmpty(query.scopeIds)) {
    parts.push(
      `"internal_id" IN (${query.scopeIds.map((id) => `'${id}'`).join(', ')})`
    );
  }

  if (!isEmpty(filterClauses)) {
    const joined = filterClauses.join(` ${linkOp} `);
    parts.push(filterClauses.length > 1 ? `(${joined})` : joined);
  }

  if ('geometry' in query && !isNil(query.geometry)) {
    parts.push(
      `ST_Within(geometry, ST_SetSRID(ST_GeomFromGeoJSON('${JSON.stringify(query.geometry)}'), 0))`
    );
  }

  return parts.length > 0 ? parts.join(' AND ') : undefined;
}

function buildOrderByClause(sorts: Sort[]): string | undefined {
  if (isEmpty(sorts)) return undefined;

  return `ORDER BY ${sorts
    .map((sort) => `"${sort.field}" ${sort.direction.toUpperCase()} NULLS LAST`)
    .join(', ')}`;
}

/**
 * ANDs `whereAdditions` (scopeIds / filters / geometry, built by
 * `buildAdditionalWhereClause`) into a raw SQL string — adding a WHERE clause
 * if none exists, or ANDing into an existing one. No-op if there's nothing to
 * inject.
 */
export function injectWhereAdditions(
  sql: string,
  whereAdditions: string | undefined
): string {
  if (isNil(whereAdditions)) return sql;

  const hasWhere = /\bWHERE\b/i.test(sql);
  const anchorRe = /\b(ORDER\s+BY|LIMIT|OFFSET)\b/i;

  if (anchorRe.test(sql)) {
    return sql.replace(
      anchorRe,
      (match) => `${hasWhere ? 'AND' : 'WHERE'} ${whereAdditions} ${match}`
    );
  }

  return (
    sql.trimEnd() +
    (hasWhere ? ` AND ${whereAdditions}` : ` WHERE ${whereAdditions}`)
  );
}

/**
 * Replaces an existing ORDER BY in a raw SQL string with `orderByClause`
 * (built by `buildOrderByClause`), inserting it before LIMIT/OFFSET (or at
 * the end) if there's no existing ORDER BY to replace. When `orderByClause`
 * is undefined (the user hasn't applied a sort in the UI): an existing
 * ORDER BY written by the AI is left untouched, otherwise a default
 * `ORDER BY "internal_id"` is applied to keep row order stable — matching
 * the same default used when building a query from scratch.
 */
function applyOrderByOverride(
  sql: string,
  orderByClause: string | undefined
): string {
  const existingOrderByRe =
    /\bORDER\s+BY\b[\s\S]*?(?=\s*\bLIMIT\b|\s*\bOFFSET\b|$)/i;
  const hasExistingOrderBy = existingOrderByRe.test(sql);

  if (isNil(orderByClause)) {
    if (hasExistingOrderBy) return sql;
    orderByClause = 'ORDER BY "internal_id"';
  } else if (hasExistingOrderBy) {
    return sql.replace(existingOrderByRe, orderByClause).trimEnd();
  }

  const anchorRe = /\b(LIMIT|OFFSET)\b/i;
  if (anchorRe.test(sql)) {
    return sql.replace(anchorRe, (match) => `${orderByClause} ${match}`);
  }

  return `${sql.trimEnd()} ${orderByClause}`;
}

/**
 * Reads the row cap imposed by an explicit LIMIT in an SQL query (e.g. "top 3 wells").
 * Consumers need this to reconcile the API's global row count, which is computed
 * as if the LIMIT wasn't there.
 */
export function getSqlRowCap(sql: string | undefined): number | undefined {
  if (isNil(sql)) return undefined;

  const rowCap = /\bLIMIT\s+(\d+)/i.exec(sql)?.[1];

  return isNil(rowCap) ? undefined : parseInt(rowCap);
}

/**
 * When an AI-generated SQL query contains an explicit LIMIT (e.g. "top 3 wells"), forced
 * pagination should not override it if that limit is smaller than pageSize — the query is
 * intentionally bounded and the app will prevent the user from switching to subsequent pages.
 * If the existing LIMIT is >= pageSize, pagination is applied normally, but the last page is
 * shortened so that paging never reads past the row cap.
 */
function applyForcedPagination(sql: string, query: ScrollableQuery): string {
  const rowCap = getSqlRowCap(sql);

  if (!isNil(rowCap) && rowCap < query.pageSize) {
    return sql;
  }

  const offset = query.page * query.pageSize;
  const limit = isNil(rowCap)
    ? query.pageSize
    : clamp(rowCap - offset, 0, query.pageSize);

  return (
    sql
      .replace(/\s+OFFSET\s+\d+/gi, '')
      .replace(/\s+LIMIT\s+\d+/gi, '')
      .trimEnd() + ` OFFSET ${offset} LIMIT ${limit}`
  );
}

function toFilterValue(params: {
  mappingItem: MappingItem;
  fromValue: Filter['value'];
  unitSystem: UnitSystem | undefined;
}): Filter['value'] {
  const { unitSystem, mappingItem, fromValue } = params;

  switch (mappingItem.domainType) {
    case 'SIUnit': {
      assert(isNumber(fromValue), 'fromValue must be a number');
      assert(unitSystem, 'unitSystem must be defined');

      const unit = toSIUnit({
        value: fromValue,
        symbol: mappingItem.domainTypeAttributes.symbol,
      });

      return undoUnitConversion(unit, unitSystem).value;
    }
    default:
      return fromValue;
  }
}

function toFilterClause(
  operator: string,
  field: string,
  value: Filter['value']
): string {
  switch (operator) {
    case '=':
    case 'is':
      return `"${field}" = ${formatSqlValue(value)}`;
    case '<=':
    case 'onOrBefore':
      return `"${field}" <= ${formatSqlValue(value)}`;
    case '>=':
    case 'onOrAfter':
      return `"${field}" >= ${formatSqlValue(value)}`;
    case 'not':
      return `"${field}" != ${formatSqlValue(value)}`;
    case 'contains':
      return `"${field}" ILIKE '%${escapeQuotes(String(value))}%'`;
    case 'notContains':
      return `"${field}" NOT ILIKE '%${escapeQuotes(String(value))}%'`;
    case 'isNil':
      return `"${field}" IS NULL`;
    default:
      return `"${field}" ${operator} ${formatSqlValue(value)}`;
  }
}

function formatSqlValue(value: Filter['value']): string {
  if (isNil(value)) return 'NULL';
  if (isBoolean(value)) return value ? 'TRUE' : 'FALSE';
  if (isNumber(value)) return String(value);
  if (isString(value)) return `'${escapeQuotes(value)}'`;
  return 'NULL'; // GeoJSON.Geometry — not supported in SQL text form
}

function escapeQuotes(value: string): string {
  return value.replace(/'/g, "''");
}
